import React, { createContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Sample preview links for when in preview mode (fallback)
const previewLinks = [
  { id: 'preview_link1', url: 'https://www.google.com', src: '/app/images/google.png', show: false },
  { id: 'preview_link2', url: 'https://www.facebook.com', src: '/app/images/facebook.png', show: false },
  { id: 'preview_link3', url: 'https://www.tripadvisor.com', src: '/app/images/tripadvisor.png', show: false },
];

export const PreviewContext = createContext({
  isPreview: false,
  logoUrl: '',
  headerUrl: '',
  accentColor: '#007BFF',
  fontStyle: 'Arial',
  previewLinks, // Keep fallback previewLinks
  customQuestions: [],
  customLinks: null, // Initialize customLinks
  templateConfig: null, // Add template configuration
});

export const PreviewProvider = ({ children }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [isPreview, setIsPreview] = useState(params.get('preview') === 'true');
  const [logoUrl, setLogoUrl] = useState('');
  const [headerUrl, setHeaderUrl] = useState('');
  const [accentColor, setAccentColor] = useState('#007BFF');
  const [fontStyle, setFontStyle] = useState('Arial');
  const [customQuestions, setCustomQuestions] = useState([]);
  const [customLinks, setCustomLinks] = useState(null); // State for custom links
  const [templateConfig, setTemplateConfig] = useState(null); // State for template config

  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    const isPreviewFromUrl = currentParams.get('preview') === 'true';

    if (isPreviewFromUrl) {
      // If URL indicates preview mode, ensure isPreview state is true.
      // PREVIEW_CONFIG message will be the primary source for data.
      // We can still set initial values from URL if they exist and if data hasn't been set by a message yet.
      setIsPreview(true);

      // Only set from URL if not already populated (e.g., by a message)
      // This part is optional and depends on whether URL params should provide a fallback
      // before the PREVIEW_CONFIG message arrives.
      if (!logoUrl && currentParams.get('logoUrl')) setLogoUrl(currentParams.get('logoUrl'));
      if (!headerUrl && currentParams.get('headerUrl')) setHeaderUrl(currentParams.get('headerUrl'));
      if (accentColor === '#007BFF' && currentParams.get('accentColor')) setAccentColor(currentParams.get('accentColor'));
      if (fontStyle === 'Arial' && currentParams.get('fontStyle')) setFontStyle(currentParams.get('fontStyle'));
      
    } else if (!isPreview) {
      // If not in preview via URL AND isPreview state is currently false (i.e., no message has set it true yet),
      // then it's safe to reset to defaults. This handles the case where the component mounts
      // without `preview=true` and before any `PREVIEW_CONFIG` message.
      setLogoUrl('');
      setHeaderUrl('');
      setAccentColor('#007BFF');
      setFontStyle('Arial');
      setCustomQuestions([]);
      setCustomLinks(null);
      setTemplateConfig(null);
    }
    // If isPreviewFromUrl is false BUT isPreview (state) is true (meaning a message set it),
    // we do nothing here to allow the message-set data to persist.
  }, [location.search, isPreview, logoUrl, headerUrl, accentColor, fontStyle]); // Added isPreview and other states to deps to refine conditions


  useEffect(() => {
    const handleMessage = (event) => {

      const { type, data } = event.data;

      if (type === 'PREVIEW_CONFIG') {
        // console.log("PreviewContext: Received PREVIEW_CONFIG", data); // Optional: for debugging
        setIsPreview(true);
        console.log("PreviewContext: Setting isPreview to true from message"); // Debugging line
        // Ensure all relevant states are updated from the message data
        if (data) {
          console.log("PreviewContext: Setting preview data from message", data); // Debugging line
          console.log("PreviewContext: Data received:", data); // Debugging line
          setLogoUrl(data.logoUrl || '');
          setHeaderUrl(data.headerUrl || '');
          setAccentColor(data.accentColor || '#007BFF'); // Use default if not provided
          setFontStyle(data.fontStyle || 'Arial');       // Use default if not provided
          setCustomQuestions(data.customQuestions || []);
          setCustomLinks(data.customLinks || null); // Keep null as default if not provided
          setTemplateConfig(data.templateConfig || null); // Set template config
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Notify parent window (dashboard) that the preview is ready
    if (window.self !== window.top) {
      console.log("Preview sending PREVIEW_READY to parent");
      window.parent.postMessage({ type: 'PREVIEW_READY' }, '*'); // Use '*' for flexibility or target specific origin
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []); // Run only once on mount

  return (
    <PreviewContext.Provider value={{
      isPreview,
      logoUrl,
      headerUrl,
      accentColor,
      fontStyle,
      previewLinks, // Provide fallback links
      customQuestions,
      customLinks, // Provide the received custom links
      templateConfig // Provide template configuration
    }}>
      {children}
    </PreviewContext.Provider>
  );
};
